import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './ProductReviews.css';

const ProductReviews = ({ productId, showSubmitForm = false, orderId = null }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('recent');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    title: '',
    comment: '',
    pros: '',
    cons: '',
    images: []
  });

  useEffect(() => {
    if (productId) {
      fetchReviews();
    }
  }, [productId, sortBy, currentPage]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(
        `/api/reviews/product/${productId}?page=${currentPage}&sort=${sortBy}`
      );
      setReviews(response.data.reviews);
      setStats(response.data.stats);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setReviewForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();

    if (!reviewForm.title || !reviewForm.comment) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/api/reviews',
        {
          product: productId,
          order: orderId,
          rating: reviewForm.rating,
          title: reviewForm.title,
          comment: reviewForm.comment,
          pros: reviewForm.pros.split(',').map(p => p.trim()).filter(p => p),
          cons: reviewForm.cons.split(',').map(c => c.trim()).filter(c => c),
          images: []
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Review submitted successfully!');
      setShowReviewForm(false);
      setReviewForm({
        rating: 5,
        title: '',
        comment: '',
        pros: '',
        cons: '',
        images: []
      });
      fetchReviews();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.response?.data?.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkHelpful = async (reviewId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `/api/reviews/${reviewId}/helpful`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Thanks for your feedback!');
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not mark as helpful');
    }
  };

  const renderStars = (rating, interactive = false, onRate = null) => {
    return (
      <div className={`stars ${interactive ? 'interactive' : ''}`}>
        {[1, 2, 3, 4, 5].map(star => (
          <span
            key={star}
            className={star <= rating ? 'star filled' : 'star'}
            onClick={() => interactive && onRate && onRate(star)}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  const renderRatingDistribution = () => {
    if (!stats) return null;

    const total = stats.totalReviews || 1;
    const distribution = [
      { stars: 5, count: stats.fiveStar || 0 },
      { stars: 4, count: stats.fourStar || 0 },
      { stars: 3, count: stats.threeStar || 0 },
      { stars: 2, count: stats.twoStar || 0 },
      { stars: 1, count: stats.oneStar || 0 }
    ];

    return (
      <div className="rating-distribution">
        {distribution.map((item) => (
          <div key={item.stars} className="distribution-bar">
            <span className="star-label">{item.stars} ★</span>
            <div className="bar-container">
              <div
                className="bar-fill"
                style={{ width: `${(item.count / total) * 100}%` }}
              ></div>
            </div>
            <span className="count">{item.count}</span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="reviews-loading">
        <div className="spinner"></div>
        <p>Loading reviews...</p>
      </div>
    );
  }

  return (
    <div className="product-reviews">
      {/* Reviews Summary */}
      {stats && (
        <div className="reviews-summary">
          <div className="summary-rating">
            <div className="avg-rating">
              <span className="rating-number">{stats.averageRating?.toFixed(1) || '0.0'}</span>
              {renderStars(Math.round(stats.averageRating || 0))}
            </div>
            <p className="total-reviews">{stats.totalReviews} reviews</p>
            <p className="verified-count">
              ✓ {stats.verifiedPurchases} verified purchases
            </p>
          </div>

          <div className="summary-distribution">
            {renderRatingDistribution()}
          </div>

          {showSubmitForm && (
            <div className="summary-actions">
              <button
                className="btn-write-review"
                onClick={() => setShowReviewForm(!showReviewForm)}
              >
                ✍️ Write a Review
              </button>
            </div>
          )}
        </div>
      )}

      {/* Review Form */}
      {showReviewForm && (
        <div className="review-form-container">
          <form onSubmit={handleSubmitReview} className="review-form">
            <h3>Write Your Review</h3>

            <div className="form-group">
              <label>Rating *</label>
              {renderStars(reviewForm.rating, true, (rating) => 
                setReviewForm(prev => ({ ...prev, rating }))
              )}
            </div>

            <div className="form-group">
              <label htmlFor="title">Review Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={reviewForm.title}
                onChange={handleFormChange}
                placeholder="Summarize your experience"
                maxLength="100"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="comment">Your Review *</label>
              <textarea
                id="comment"
                name="comment"
                value={reviewForm.comment}
                onChange={handleFormChange}
                placeholder="Tell us what you think about this product..."
                rows="5"
                maxLength="1000"
                required
              ></textarea>
              <small>{reviewForm.comment.length}/1000 characters</small>
            </div>

            <div className="form-group">
              <label htmlFor="pros">Pros (comma separated)</label>
              <input
                type="text"
                id="pros"
                name="pros"
                value={reviewForm.pros}
                onChange={handleFormChange}
                placeholder="Good quality, Fast delivery, Value for money"
              />
            </div>

            <div className="form-group">
              <label htmlFor="cons">Cons (comma separated)</label>
              <input
                type="text"
                id="cons"
                name="cons"
                value={reviewForm.cons}
                onChange={handleFormChange}
                placeholder="Packaging, Size issues"
              />
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowReviewForm(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Reviews List */}
      <div className="reviews-list-section">
        <div className="reviews-header">
          <h3>Customer Reviews ({stats?.totalReviews || 0})</h3>
          <div className="sort-controls">
            <label>Sort by:</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="recent">Most Recent</option>
              <option value="helpful">Most Helpful</option>
              <option value="rating-high">Highest Rating</option>
              <option value="rating-low">Lowest Rating</option>
            </select>
          </div>
        </div>

        <div className="reviews-list">
          {reviews.length === 0 ? (
            <div className="no-reviews">
              <p>No reviews yet. Be the first to review this product!</p>
            </div>
          ) : (
            reviews.map((review) => (
              <div key={review._id} className="review-card">
                <div className="review-header">
                  <div className="reviewer-info">
                    <div className="reviewer-avatar">
                      {review.user?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="reviewer-details">
                      <span className="reviewer-name">{review.user?.name || 'Anonymous'}</span>
                      {review.verifiedPurchase && (
                        <span className="verified-badge">✓ Verified Purchase</span>
                      )}
                    </div>
                  </div>
                  <div className="review-date">
                    {new Date(review.createdAt).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                </div>

                <div className="review-rating">
                  {renderStars(review.rating)}
                  <span className="rating-value">{review.rating}.0</span>
                </div>

                <h4 className="review-title">{review.title}</h4>
                <p className="review-comment">{review.comment}</p>

                {(review.pros?.length > 0 || review.cons?.length > 0) && (
                  <div className="review-pros-cons">
                    {review.pros?.length > 0 && (
                      <div className="pros">
                        <strong>👍 Pros:</strong>
                        <ul>
                          {review.pros.map((pro, index) => (
                            <li key={index}>{pro}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {review.cons?.length > 0 && (
                      <div className="cons">
                        <strong>👎 Cons:</strong>
                        <ul>
                          {review.cons.map((con, index) => (
                            <li key={index}>{con}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {review.images?.length > 0 && (
                  <div className="review-images">
                    {review.images.map((img, index) => (
                      <img key={index} src={img} alt={`Review ${index + 1}`} />
                    ))}
                  </div>
                )}

                <div className="review-actions">
                  <button
                    className="btn-helpful"
                    onClick={() => handleMarkHelpful(review._id)}
                  >
                    👍 Helpful ({review.helpful || 0})
                  </button>
                </div>

                {review.sellerReply && (
                  <div className="seller-reply">
                    <div className="reply-header">
                      <strong>Seller Response</strong>
                      <span className="reply-date">
                        {new Date(review.sellerReply.repliedAt).toLocaleDateString()}
                      </span>
                    </div>
                    <p>{review.sellerReply.reply}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductReviews;
